import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, GraduationCap, LogOut, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const ADMIN_TOKEN_KEY = 'admin_reports_token';
const ADMIN_USER_KEY = 'admin_reports_user';

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(Number(value || 0));
const formatDateTimeIST = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const toEpoch = (value) => {
  const ts = Date.parse(String(value || ''));
  return Number.isFinite(ts) ? ts : 0;
};

const buildLeadDescription = (row) => {
  const parts = [];
  if (row.level_class) parts.push(`Class/Level: ${row.level_class}`);
  if (row.location) parts.push(`Location: ${row.location}`);
  if (row.status) parts.push(`Status: ${row.status}`);
  if (Array.isArray(row.mode) && row.mode.length > 0) parts.push(`Mode: ${row.mode.join(', ')}`);
  if (row.board) parts.push(`Board: ${row.board}`);
  if (row.time_preference) parts.push(`Time: ${row.time_preference}`);
  if (row.budget_min || row.budget_max) {
    parts.push(`Budget: ₹${row.budget_min || 0}-${row.budget_max || row.budget_min || 0}`);
  }
  if (row.description) parts.push(`Notes: ${row.description}`);
  return parts.join(' | ') || '-';
};

export default function AdminLeads() {
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [leadRegistry, setLeadRegistry] = useState([]);

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(ADMIN_USER_KEY);
    setAdminToken('');
    setLeadRegistry([]);
  };

  const loadLeadRegistry = async (token = adminToken) => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/admin/reports/funnel?days=30', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || 'Failed to load lead details');
      }
      const rows = Array.isArray(data?.lead_registry) ? data.lead_registry : [];
      rows.sort((a, b) => toEpoch(b.lead_created_at) - toEpoch(a.lead_created_at));
      setLeadRegistry(rows);
    } catch (err) {
      const message = err.message || 'Failed to load lead details';
      setError(message);
      if (message.toLowerCase().includes('session') || message.toLowerCase().includes('unauthorized')) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminToken) {
      loadLeadRegistry(adminToken);
    }
  }, [adminToken]);

  if (!adminToken) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
            <div>
              <div className="text-xl font-bold text-slate-900">Tricity Tutors</div>
              <div className="text-xs text-slate-500">Lead Details</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/admin">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back To Admin
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/tutors">Tutors Details</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/students">Student Details</Link>
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle>Lead Details</CardTitle>
              <CardDescription>All leads with student registration date/time in IST and total leads placed.</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline">{formatNumber(leadRegistry.length)} leads</Badge>
              <Button onClick={() => loadLeadRegistry()} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Loading' : 'Refresh'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="text-sm text-red-600">{error}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student Name</TableHead>
                    <TableHead>Registration Date/Time (IST)</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Lead Date/Time (IST)</TableHead>
                    <TableHead>Leads Placed</TableHead>
                    <TableHead>Teachers Contacted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leadRegistry.map((row, index) => (
                    <TableRow key={`${row.email}-${row.lead_created_at}-${row.subject}-${index}`}>
                      <TableCell>{row.student_name}</TableCell>
                      <TableCell>{formatDateTimeIST(row.registration_created_at)}</TableCell>
                      <TableCell>{row.mobile || '-'}</TableCell>
                      <TableCell>{row.email || '-'}</TableCell>
                      <TableCell>{row.subject || '-'}</TableCell>
                      <TableCell className="max-w-xl whitespace-normal text-sm text-slate-600">{buildLeadDescription(row)}</TableCell>
                      <TableCell>{formatDateTimeIST(row.lead_created_at)}</TableCell>
                      <TableCell>{formatNumber(row.leads_placed)}</TableCell>
                      <TableCell>{formatNumber(row.teachers_contacted)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
